import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useStorage, useDebounceFn } from "@vueuse/core"
import { useSupplierProductsStore, IMPORT_LIST_TYPE } from '@/stores/storage/SupplierProductsStore'
import { useRouter, useRoute } from "vue-router"


/**
 * Supplier Products Import List Store
 *
 * Manages import list products data including:
 * - Fetching import list products (with BE pagination)
 * - Server-side filtering (suppliers, producers, categories)
 * - Server-side sorting
 * - Server-side search
 * - Tracking products with changed states
 * - Import simulation
 */
export const useSupplierProductsImportListStore = defineStore('supplierProductsImportListStore', () => {

  // ========================================
  // State
  // ========================================

  /** Global loading state for import list operations */
  const isLoading = ref(true)

  /** Loading state for import operation */
  const isImporting = ref(false)

  /**
   * How many products are in import list
   */
  const importListCounter = ref(0)

  /**
   * Import results from the last import operation
   * @type {Object|null}
   */
  const importResults = ref(null)

  /**
   * Whether to show import results view
   */
  const showImportResults = ref(false)

  /**
   * Set of product IDs currently being updated
   * @type {Set<number>}
   */
  const updatingProductIds = ref(new Set())

  /**
   * Products for the current page from API
   * @type {Array}
   */
  const allProducts = ref([])

  /**
   * Whether data has been fetched
   */
  const isLoaded = ref(false)

  const router = useRouter()
  const route = useRoute()

  /**
   * Pagination metadata from API
   */
  const meta = ref({
    total: 0,
    perPage: 11,
    page: 1,
  })

  /**
   * Available filters from API
   * @property {Array<{id: string, name: string}>} suppliers - List of supplier objects
   * @property {Array<{id: string, name: string}>} producers - List of producer objects
   * @property {Array<{id: string, name: string}>} categories - List of category objects
   */
  const availableFilters = ref({
    suppliers: [],
    producers: [],
    categories: [],
  })

  /** Loading state for filters */
  const isLoadingFilters = ref(false)

  /** Search query for filtering products */
  const searchQuery = useStorage('storage-import-list-search', '', localStorage, {
    listenToStorageChanges: false,
  })

  /** Selected supplier filter */
  const selectedSupplier = useStorage('storage-import-list-selected-supplier', null, localStorage, {
    listenToStorageChanges: false,
  })

  /** Selected producer filter */
  const selectedProducer = useStorage('storage-import-list-selected-producer', null, localStorage, {
    listenToStorageChanges: false,
  })

  /** Selected category filter */
  const selectedCategory = useStorage('storage-import-list-selected-category', null, localStorage, {
    listenToStorageChanges: false,
  })

  /**
   * Sorting configuration
   * @property {string} column - Column to sort by (producer, category, name, buyPrice, supplierName)
   * @property {string} direction - Sort direction (asc, desc)
   */
  const sorting = ref({
    column: null,
    direction: 'asc',
  })

  /**
   * Original product types from API
   * Key: product ID (import list item id), Value: original type
   * Used to track which products have changed state
   * @type {Map<number, number>}
   */
  const originalProductTypes = ref(new Map())

  /**
   * Current type selection per product
   * Key: product ID (import list item id), Value: type (1, 2, or 3)
   * @type {Map<number, number>}
   */
  const productTypeSelections = ref(new Map())

  // ========================================
  // Getters - Pagination
  // ========================================

  /**
   * Total pages based on pagination metadata
   * @returns {number}
   */
  const totalPages = computed(() => {
    if (!meta.value.total || !meta.value.perPage) return 1

    return Math.ceil(meta.value.total / meta.value.perPage)
  })

  /**
   * Current page from pagination metadata
   * @returns {number}
   */
  const currentPage = computed(() => meta.value.page)

  /**
   * Total count of products in import list (all, regardless of filters)
   * @returns {number}
   */
  const totalProductsCount = computed(() => importListCounter.value)

  /**
   * Filtered products count (matching current filters)
   * @returns {number}
   */
  const filteredProductsCount = computed(() => meta.value.total)

  // ========================================
  // Actions
  // ========================================

  /**
   * Fetch available filters from the backend
   *
   * @returns {Promise<boolean>} True if successful, false otherwise
   */
  const fetchFilters = async () => {
    isLoadingFilters.value = true

    try {
      const response = await $backend('/supplierProductsList/filters', {
        method: 'GET',
      })

      if (response.success) {
        availableFilters.value = {
          suppliers: response.data.filters.suppliers || [],
          producers: response.data.filters.producers || [],
          categories: response.data.filters.categories || [],
        }

        return true
      }
    } catch (error) {
      console.error('Error fetching import list filters:', error)
      return false
    } finally {
      isLoadingFilters.value = false
    }

    return false
  }

  /**
   * Fetch import list products from the backend (with pagination and server-side filters)
   *
   * @param {Object} options - Fetch options
   * @param {number} options.page - Page number
   * @param {number} options.perPage - Items per page
   * @param {boolean} options.force - Force refetch even if loaded
   * @returns {Promise<boolean>} True if successful, false otherwise
   */
  const fetchImportListProducts = async ({ page = 1, perPage = 11, force = false } = {}) => {
    // Skip fetching if data is already loaded for the same page
    if (!force && isLoaded.value && meta.value.page === page) {
      return true
    }

    isLoading.value = true

    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        perPage: perPage.toString(),
      })

      // Add sorting if specified
      if (sorting.value.column) {
        params.append('orders[0][column]', sorting.value.column)
        params.append('orders[0][direction]', sorting.value.direction)
      }

      // Add filters if specified
      if (searchQuery.value.trim()) {
        params.append('filters[search]', searchQuery.value.trim())
      }
      if (selectedSupplier.value) {
        params.append('filters[supplierId]', selectedSupplier.value)
      }
      if (selectedProducer.value) {
        params.append('filters[producer]', selectedProducer.value)
      }
      if (selectedCategory.value) {
        params.append('filters[category]', selectedCategory.value)
      }

      const response = await $backend(`/supplierProductsList/index?${params.toString()}`, {
        method: 'GET',
      })

      if (response.success) {
        // If we are on far away page and there is no results for this page we go to last page instead
        if (response.data.products.length === 0 && page !== 1 && response.meta?.total > 0) {
          isLoaded.value = false
          const lastPage = Math.ceil(response.meta.total / (response.meta?.perPage ?? perPage))
          await router.push({ path: '/storage/import-list', query: { page: lastPage, back: route.query.back } })

          return true
        }

        allProducts.value = response.data.products || []
        meta.value = response.meta || {
          total: 0,
          perPage: perPage,
          page: page,
        }
        isLoaded.value = true

        importListCounter.value = response.meta.totalAll ?? importListCounter.value

        // Store original types and sync current selections for loaded products
        syncProductTypesWithFetchedData()

        return true
      }
    } catch (error) {
      console.error('Error fetching import list products:', error)

      return false
    } finally {
      isLoading.value = false
    }

    return false
  }

  /**
   * Sync original types and current selections with fetched product data
   */
  const syncProductTypesWithFetchedData = () => {
    allProducts.value.forEach((product) => {
      const originalType = product.type ?? IMPORT_LIST_TYPE.USAGE

      // Store original state
      originalProductTypes.value.set(product.id, originalType)

      // Set current selection to match original
      productTypeSelections.value.set(product.id, originalType)
    })
  }

  /**
   * Change page and refetch products
   *
   * @param {number} page - Page number
   * @returns {Promise<boolean>}
   */
  const changePage = async (page) => {
    return await fetchImportListProducts({
      page,
      perPage: meta.value.perPage,
      force: true,
    })
  }

  /**
   * Change items per page and refetch products (reset to page 1)
   *
   * @param {number} perPage - Items per page
   * @returns {Promise<boolean>}
   */
  const changePerPage = async (perPage) => {
    return await fetchImportListProducts({
      page: 1,
      perPage,
      force: true,
    })
  }

  /**
   * Set search query and refetch products (reset to page 1)
   *
   * @param {string} query - Search query
   * @returns {Promise<boolean>}
   */
  const setSearchQuery = async (query) => {
    searchQuery.value = query

    return await fetchImportListProducts({
      page: 1,
      perPage: meta.value.perPage,
      force: true,
    })
  }

  /**
   * Debounced action: updates search query and refetches products after 300 ms of inactivity
   *
   * @param {string} query - Search query
   * @returns {Promise<boolean>}
   */
  const debouncedSetSearchQuery = useDebounceFn(async (query) => {
    searchQuery.value = query

    return await fetchImportListProducts({
      page: 1,
      perPage: meta.value.perPage,
      force: true,
    })
  }, 300)

  /**
   * Set supplier filter and refetch products (reset to page 1)
   *
   * @param {string|null} supplierId - Supplier ID or null to clear
   * @returns {Promise<boolean>}
   */
  const setSupplierFilter = async (supplierId) => {
    selectedSupplier.value = supplierId

    return await fetchImportListProducts({
      page: 1,
      perPage: meta.value.perPage,
      force: true,
    })
  }

  /**
   * Set producer filter and refetch products (reset to page 1)
   *
   * @param {string|null} producer - Producer ID or null to clear
   * @returns {Promise<boolean>}
   */
  const setProducerFilter = async (producer) => {
    selectedProducer.value = producer

    return await fetchImportListProducts({
      page: 1,
      perPage: meta.value.perPage,
      force: true,
    })
  }

  /**
   * Set category filter and refetch products (reset to page 1)
   *
   * @param {string|null} category - Category ID or null to clear
   * @returns {Promise<boolean>}
   */
  const setCategoryFilter = async (category) => {
    selectedCategory.value = category

    return await fetchImportListProducts({
      page: 1,
      perPage: meta.value.perPage,
      force: true,
    })
  }

  /**
   * Set sorting and refetch products (reset to page 1)
   *
   * @param {string|null} column - Column to sort by
   * @param {string} direction - Sort direction (asc/desc)
   * @returns {Promise<boolean>}
   */
  const setSorting = async (column, direction = 'asc') => {
    sorting.value = { column, direction }

    return await fetchImportListProducts({
      page: 1,
      perPage: meta.value.perPage,
      force: true,
    })
  }

  /**
   * Clear all filters and refetch products
   *
   * @returns {Promise<boolean>}
   */
  const clearAllFilters = async () => {
    searchQuery.value = ''
    selectedSupplier.value = null
    selectedProducer.value = null
    selectedCategory.value = null
    sorting.value = { column: null, direction: 'asc' }

    return await fetchImportListProducts({
      page: 1,
      perPage: meta.value.perPage,
      force: true,
    })
  }

  /**
   * Set type selection for a product (radio button behavior - always has a value)
   *
   * @param {number} productId - Product ID (import list item id)
   * @param {number} type - Type (IMPORT_LIST_TYPE.SALE, USAGE, or BOTH)
   */
  const setProductType = (productId, type) => {
    productTypeSelections.value.set(productId, type)
  }

  /**
   * Get type selection for a product
   *
   * @param {number} productId - Product ID (import list item id)
   * @returns {number} Type or IMPORT_LIST_TYPE.USAGE as default
   */
  const getProductType = (productId) => {
    return productTypeSelections.value.get(productId) ?? IMPORT_LIST_TYPE.USAGE
  }

  /**
   * Get original type for a product (from API)
   *
   * @param {number} productId - Product ID (import list item id)
   * @returns {number} Original type or IMPORT_LIST_TYPE.USAGE as default
   */
  const getOriginalProductType = (productId) => {
    return originalProductTypes.value.get(productId) ?? IMPORT_LIST_TYPE.USAGE
  }

  /**
   * Check if a product has changed type
   *
   * @param {number} productId - Product ID
   * @returns {boolean}
   */
  const hasProductChangedType = (productId) => {
    const currentType = getProductType(productId)
    const originalType = getOriginalProductType(productId)

    return currentType !== originalType
  }

  /**
   * Check if a product is currently being updated
   *
   * @param {number} productId - Product ID
   * @returns {boolean}
   */
  const isProductUpdating = (productId) => {
    return updatingProductIds.value.has(productId)
  }

  /**
   * Check if any product is currently being updated
   * @returns {boolean}
   */
  const isAnyProductUpdatingNow = computed(() => {
    return updatingProductIds.value.size > 0
  })

  /**
   * Update product type in import list
   * Calls the SupplierProductsStore's updateProductState method
   *
   * @param {number} productId - Product ID (import list item id)
   * @returns {Promise<boolean>}
   */
  const updateProductType = async (productId) => {
    const currentType = getProductType(productId)
    const originalType = getOriginalProductType(productId)

    if (currentType === originalType) return false
    if (updatingProductIds.value.has(productId)) return false

    updatingProductIds.value.add(productId)

    try {
      // Find the product to get supplierProductId
      const product = allProducts.value.find(p => p.id === productId)

      if (!product) return false

      const productToChange = {
        listItemId: productId,
        supplierProductId: product.supplierProductId,
        newType: currentType,
        originalType: originalType,
      }
      console.log('Updating product type in import list:', productToChange)

      const response = await $backend('/supplierProductsList/update', {
        method: 'POST',
        body: [productToChange],
      })

      if (response && response.success) {
        // Update original type to match current selection (simulating successful API response)
        originalProductTypes.value.set(productId, currentType)

        // Update the product object in the list
        if (product) {
          product.type = currentType
        }

        return true
      }

      return false
    } catch (error) {
      //console.error('Error updating product type:', error)

      return false
    } finally {
      updatingProductIds.value.delete(productId)
    }
  }

  /**
   * Set all visible (current page) products to one type and persist via API.
   * Uses the same backend as updateProductType but sends a batch of changes.
   *
   * @param {number} importListType - Import list type (IMPORT_LIST_TYPE.SALE, USAGE, or BOTH)
   * @returns {Promise<boolean>}
   */
  const setAllVisibleProductsType = async (importListType) => {
    const products = allProducts.value
    if (products.length === 0) return true

    // Set local type for all
    products.forEach(p => setProductType(p.id, importListType))

    const changes = products
      .filter(p => getProductType(p.id) !== getOriginalProductType(p.id))
      .map(p => ({
        listItemId: p.id,
        supplierProductId: p.supplierProductId,
        newType: getProductType(p.id),
        originalType: getOriginalProductType(p.id),
      }))

    if (changes.length === 0) return true

    changes.forEach(c => updatingProductIds.value.add(c.listItemId))

    try {
      const response = await $backend('/supplierProductsList/update', {
        method: 'POST',
        body: changes,
      })

      if (response && response.success) {
        changes.forEach(({ listItemId, newType }) => {
          originalProductTypes.value.set(listItemId, newType)
          const product = allProducts.value.find(p => p.id === listItemId)
          if (product) product.type = newType
        })
        return true
      }
      return false
    } catch {
      return false
    } finally {
      changes.forEach(c => updatingProductIds.value.delete(c.listItemId))
    }
  }

  /**
   * Remove product from import list
   * Calls the SupplierProductsStore's removeProductFromImportList method
   *
   * @param {number} productId - Product ID (import list item id)
   * @returns {Promise<boolean>}
   */
  const removeProductFromImportList = async (productId) => {
    if (updatingProductIds.value.has(productId)) return false

    updatingProductIds.value.add(productId)

    try {
      // Find the product to get supplierProductId
      const product = allProducts.value.find(p => p.id === productId)

      if (!product) return false

      // Get the SupplierProductsStore to use its removeProductFromImportList method
      const supplierProductsStore = useSupplierProductsStore()

      // Call the store method with the supplierProductId
      const success = await supplierProductsStore.removeProductFromImportList(product.supplierProductId)

      if (success) {
        // Clean up maps
        originalProductTypes.value.delete(productId)
        productTypeSelections.value.delete(productId)

        // Refetch current page (may need to go to previous page if last item on page)
        const currentPageProducts = allProducts.value.length
        const page = currentPageProducts <= 1 && meta.value.page > 1
          ? meta.value.page - 1
          : meta.value.page

        await fetchImportListProducts({ page, perPage: meta.value.perPage, force: true })
      }

      return success
    } catch (error) {
      console.error('Error removing product from import list:', error)

      return false
    } finally {
      updatingProductIds.value.delete(productId)
    }
  }

  /**
   * Remove all products from import list
   *
   * @returns {Promise<boolean>}
   */
  const removeAllVisibleProductsFromImportList = async () => {
    if (allProducts.value.length === 0) {
      console.log('No products in import list to remove')

      return false
    }

    isImporting.value = true

    try {
      // Get the SupplierProductsStore to use its removeProductFromImportList method
      const supplierProductsStore = useSupplierProductsStore()

      // Collect all supplierProductIds to remove
      const supplierProductIds = allProducts.value.map(p => p.supplierProductId)

      // Call the store method to remove all products at once
      const success = await supplierProductsStore.removeMultipleProductsFromImportList(supplierProductIds)

      if (success) {
        // Clear all local state
        allProducts.value = []
        originalProductTypes.value.clear()
        productTypeSelections.value.clear()
      }

      return success
    } catch (error) {
      //console.error('Error removing all products from import list:', error)

      return false
    } finally {
      isImporting.value = false
    }
  }

  /**
   * Remove all products from import list
   *
   * @returns {Promise<boolean>}
   */
  const removeAllProductsFromImportList = async () => {
    if (allProducts.value.length === 0) {
      console.log('No products in import list to remove')

      return false
    }

    isImporting.value = true


    try {
      const response = await $backend('/supplierProductsList/deleteAll', {
        method: 'POST'
      })

      if (response && response.success) {

        importListCounter.value = response.data.listCounter
        allProducts.value = []
        originalProductTypes.value.clear()
        productTypeSelections.value.clear()
        meta.value.page = 1
        meta.value.total = 0

        return true
      }

      return false
    } catch (error) {
      isImporting.value = false

      return false
    }
    finally {
      isImporting.value = false
    }
  }

  /**
   * Import all import list products to user's warehouse
   *
   * @returns {Promise<{success: boolean, message: string}>}
   */
  const importToWarehouse = async () => {
    console.log('importToWarehouse!!')
    if (allProducts.value.length === 0) { // it should never run
      return {
        success: false,
        message: 'No products in import list to import',
      }
    }

    isImporting.value = true

    try {
      const response = await $backend('/supplierProductsList/import', {
        method: 'GET',
      })

      if (response.success) {
        // Store import results
        importResults.value = {
          message: response.message || '',
          listCounter: response.data?.listCounter ?? 0,
          summary: response.data?.summary || { imported: 0, skipped: 0 },
          skippedDetails: response.data?.skippedDetails || [],
        }

        // Update import list counter
        importListCounter.value = response.data?.listCounter ?? 0

        // Clear local products list since they've been imported
        allProducts.value = []
        originalProductTypes.value.clear()
        productTypeSelections.value.clear()

        // Show import results view
        showImportResults.value = true

        return {
          success: true,
          message: response.message || '',
        }
      }

      return {
        success: false,
        message: response.message || 'Import failed',
      }
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Error importing products to warehouse',
      }
    } finally {
      isImporting.value = false
    }
  }

  /**
   * Clear import results and hide results view
   */
  const clearImportResults = () => {
    importResults.value = null
    showImportResults.value = false
  }

  /**
   * Reset store to initial state
   */
  const resetStore = () => {
    isLoading.value = false
    isImporting.value = false
    allProducts.value = []
    isLoaded.value = false
    meta.value = { total: 0, perPage: 10, page: 1 }
    availableFilters.value = { suppliers: [], producers: [], categories: [] }
    // Persisted filters are intentionally NOT cleared here
    // (they survive navigation, just like in other storage stores).
    // Use clearAllFilters() for user-initiated clearing.
    sorting.value = { column: null, direction: 'asc' }
    originalProductTypes.value.clear()
    productTypeSelections.value.clear()
    updatingProductIds.value.clear()
    importResults.value = null
    showImportResults.value = false
  }

  // ========================================
  // Exports
  // ========================================

  return {
    // State
    isLoading,
    isLoadingFilters,
    isImporting,
    importListCounter,
    updatingProductIds,
    allProducts,
    isLoaded,
    meta,
    availableFilters,
    searchQuery,
    selectedSupplier,
    selectedProducer,
    selectedCategory,
    sorting,
    originalProductTypes,
    productTypeSelections,
    importResults,
    showImportResults,

    // Getters - Pagination
    totalPages,
    currentPage,
    totalProductsCount,
    filteredProductsCount,

    // Actions
    fetchFilters,
    fetchImportListProducts,
    changePage,
    changePerPage,
    setSearchQuery,
    debouncedSetSearchQuery,
    setSupplierFilter,
    setProducerFilter,
    setCategoryFilter,
    setSorting,
    clearAllFilters,
    setProductType,
    getProductType,
    getOriginalProductType,
    hasProductChangedType,
    isProductUpdating,
    isAnyProductUpdatingNow,
    updateProductType,
    setAllVisibleProductsType,
    removeProductFromImportList,
    removeAllVisibleProductsFromImportList,
    removeAllProductsFromImportList,
    importToWarehouse,
    clearImportResults,
    resetStore,
  }
})

// Re-export IMPORT_LIST_TYPE for convenience
export { IMPORT_LIST_TYPE }
